/**
 * "Priya saved v4 · 2 changes" (memo co-editing): what teammates saved since
 * this person last looked at the memo, with a way to see the changes and a
 * way into the shared draft.
 *
 * "Last looked" is the newest version on screen the last time this browser
 * opened the deal, kept in local storage; a first visit shows nothing. A
 * version this person saved themselves is not news to them.
 */

import { PenLine, X } from "lucide-react";
import React from "react";

import type { DealScreeningRun, DealScreeningRunVersion } from "../../types/dealScreening";
import { formatIsoTimestamp } from "./dealScreeningFormat";
import { stripMarks } from "./inlineMarks";

const seenKey = (dealId: string) => `doppler.dealScreening.lastSeenVersion.${dealId}`;

export const lastSeenVersion = (dealId: string): number | null => {
	try {
		const raw = window.localStorage.getItem(seenKey(dealId));
		const value = raw === null ? NaN : Number(raw);
		return Number.isFinite(value) ? value : null;
	} catch {
		return null;
	}
};

export const rememberSeenVersion = (dealId: string, version: number): void => {
	try {
		window.localStorage.setItem(seenKey(dealId), String(version));
	} catch {
		// Storage blocked: the banner just shows again next time.
	}
};

/** The versions saved by someone else since `seenVersion`, newest first. */
export function savedSince(
	versions: DealScreeningRunVersion[],
	seenVersion: number | null,
	meId: string,
): DealScreeningRunVersion[] {
	if (seenVersion === null) return [];
	return versions
		.filter((v) => v.version > seenVersion && v.generated_at && !v.generation_error)
		.filter((v) => (v.origin === "edited" ? v.saved_by_user_id !== meId : v.generated_by_user_id !== meId))
		.sort((a, b) => b.version - a.version);
}

/** "2 changes in Overview and Risks", from the version's contributors. */
export function changesLine(version: DealScreeningRunVersion, run: DealScreeningRun): string | null {
	const contributors = version.contributors ?? [];
	const total = contributors.reduce((sum, c) => sum + c.changes, 0);
	if (total === 0) return null;
	const keys = Array.from(new Set(contributors.flatMap((c) => Object.keys(c.sections))));
	const titles = keys
		.map((key) => stripMarks(run.sections.find((s) => s.key === key)?.title ?? ""))
		.filter(Boolean);
	const where =
		titles.length === 0 ? "" : titles.length === 1 ? ` in ${titles[0]}` : ` in ${titles.slice(0, -1).join(", ")} and ${titles[titles.length - 1]}`;
	return `${total === 1 ? "1 change" : `${total} changes`}${where}`;
}

export interface MemoSavedBannerProps {
	run: DealScreeningRun;
	versions: DealScreeningRunVersion[];
	meId: string;
	seenVersion: number | null;
	/** Open the comparison from the version last seen to the newest. */
	onShowChanges: (fromVersion: number) => void;
	onJoin: () => void;
	onDismiss: () => void;
}

const MemoSavedBanner: React.FC<MemoSavedBannerProps> = ({
	run,
	versions,
	meId,
	seenVersion,
	onShowChanges,
	onJoin,
	onDismiss,
}) => {
	const since = savedSince(versions, seenVersion, meId);
	if (since.length === 0 || seenVersion === null) return null;
	const newest = since[0];
	const who =
		newest.origin === "edited" ? (newest.saved_by_name ?? "A teammate") : (newest.generated_by_name ?? "Doppler");
	const verb = newest.origin === "edited" ? "saved" : "ran";
	const changes = changesLine(newest, run);
	return (
		<div
			role="status"
			className="mx-6 mt-3 rounded-xl border border-[#d9d0fb] bg-[#f4f0ff] px-4 py-2.5 text-[13px] text-[#3b2a7a]"
			data-testid="memo-saved-banner"
		>
			<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
				<span className="font-semibold">
					{who} {verb} v{newest.version}
					{changes ? ` · ${changes}` : ""}
				</span>
				<span className="text-[#6B5BA8]">{formatIsoTimestamp(newest.created_at)}</span>
				<button
					type="button"
					onClick={() => onShowChanges(seenVersion)}
					className="font-medium text-[#5B3FA8] underline-offset-2 hover:underline"
				>
					Show changes
				</button>
				<button
					type="button"
					onClick={onJoin}
					className="inline-flex items-center gap-1 rounded-md border border-[#d9d0fb] bg-white px-2 py-0.5 text-xs font-semibold text-[#5B3FA8] hover:bg-[#faf8ff]"
				>
					<PenLine className="h-3 w-3" aria-hidden />
					Join editing
				</button>
				<button
					type="button"
					onClick={onDismiss}
					aria-label="Dismiss"
					className="ml-auto rounded p-0.5 text-[#8a7fb0] hover:bg-white"
				>
					<X className="h-3.5 w-3.5" aria-hidden />
				</button>
			</div>
			{since.length > 1 && (
				<ul className="mt-1.5 space-y-0.5 border-t border-[#e4dcfd] pt-1.5 text-xs text-[#5B4E8F]" data-testid="since-you-looked">
					<li className="font-semibold uppercase tracking-[.08em] text-[#8a7fb0]">Since you last looked (v{seenVersion})</li>
					{since.slice(1, 6).map((version) => (
						<li key={version.public_id}>
							{version.origin === "edited" ? (version.saved_by_name ?? "A teammate") : (version.generated_by_name ?? "Doppler")}{" "}
							{version.origin === "edited" ? "saved" : "ran"} v{version.version}
							{changesLine(version, run) ? ` · ${changesLine(version, run)}` : ""} ·{" "}
							{formatIsoTimestamp(version.created_at)}
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

export default MemoSavedBanner;
